/**
 * Nommer un morceau à l'intérieur d'une phrase.
 *
 * La description d'une fiche doit pouvoir dire « Apprends le Lac des cygnes au
 * piano » : c'est la formulation que quelqu'un tape, donc celle que Google met
 * en gras dans l'extrait. Encore faut-il que le titre entre dans la phrase, ce
 * qui demande trois choses que le catalogue ne fournit pas.
 *
 * # L'article
 *
 * « Apprends Gymnopédie n° 1 au piano » n'est pas du français. Le déterminant
 * dépend du genre du nom de tête, qu'aucun champ ne porte — mais ce nom de tête
 * est presque toujours une **forme musicale**, et les formes sont un ensemble
 * fermé de quelques dizaines de mots. D'où la table ci-dessous : elle stocke
 * l'article tout fait plutôt qu'un genre, ce qui évite d'avoir à décider de
 * l'élision (« l'Étude », mais « la Sonate »).
 *
 * Hors de la table, on n'invente rien : le titre est alors un nom propre ou un
 * titre étranger, et « Apprends Nimrod au piano » se passe très bien d'article.
 *
 * # Le titre que l'on cherche
 *
 * Un tiers des titres porte une apposition après virgule — « Le Lac des cygnes,
 * thème », « Carmen, ouverture ». Au milieu d'une phrase elle la casse
 * (« Apprends le Lac des cygnes, thème au piano »), et personne ne la tape.
 * On coupe donc à la virgule, sauf quand la tête est trop courte pour se
 * chercher seule — « L'Été » ou « Carmen » ne désignent rien tout nus.
 *
 * Les parenthèses, elles, restent : « la Barcarolle (version facile) » se lit
 * dans la phrase, et c'est souvent tout ce qui distingue deux fiches.
 *
 * # Ce qui ne rentre pas
 *
 * Un titre qui commence par une préposition (« Dans l'antre du roi de la
 * montagne », « Des pas sur la neige ») ne prend pas d'article, et l'insérer
 * dans « Apprends … au piano » donne une phrase fausse. `titlePhrase` rend
 * alors `null` : l'appelant tourne la phrase autrement plutôt que de forcer.
 *
 * Module pur : ni React, ni DOM. Testé dans `__tests__/piece-title-phrase.test.ts`.
 */

/**
 * En deçà, la tête d'un titre à virgule ne se cherche pas seule : « L'Été »,
 * « Carmen ». On garde alors le titre entier, quitte à renoncer à la phrase.
 */
const MIN_HEAD_LENGTH = 12;

/** Les déterminants qu'un titre porte déjà. On ne lui en ajoute pas un second. */
const LEADING_DETERMINERS = ["l'", "l’", "les ", "le ", "la ", "une ", "un "];

/**
 * Les titres qui ouvrent sur une préposition : rien ne peut les précéder.
 *
 * « des » est ici et pas parmi les déterminants — « Apprends des pas sur la
 * neige » se lit comme une leçon de marche, pas comme un titre de Debussy. Et
 * « d » plutôt que « d' » : le mot de tête est découpé à l'apostrophe.
 */
const BLOCKING_OPENERS = new Set([
  "à",
  "au",
  "aux",
  "avec",
  "chez",
  "contre",
  "d",
  "dans",
  "de",
  "des",
  "du",
  "en",
  "entre",
  "par",
  "pour",
  "sans",
  "sous",
  "sur",
  "vers",
]);

/**
 * L'article des formes musicales, tout fait.
 *
 * Ne mettre ici que des **formes** (le nom qui dit ce qu'est le morceau), jamais
 * un mot de titre : « clair » n'y est pas, parce que « Clair de lune » est un
 * nom propre et se passe d'article.
 */
const FORM_ARTICLES: Record<string, string> = {
  adagio: "l'",
  air: "l'",
  allegretto: "l'",
  allegro: "l'",
  andante: "l'",
  arabesque: "l'",
  arietta: "l'",
  ballade: "la ",
  barcarolle: "la ",
  berceuse: "la ",
  bourrée: "la ",
  cancan: "le ",
  canon: "le ",
  cantate: "la ",
  caprice: "le ",
  chanson: "la ",
  chant: "le ",
  choral: "le ",
  concerto: "le ",
  consolation: "la ",
  contredanse: "la ",
  danse: "la ",
  deuxième: "la ",
  divertissement: "le ",
  école: "l'",
  élégie: "l'",
  étude: "l'",
  exercice: "l'",
  fantaisie: "la ",
  fugue: "la ",
  galop: "le ",
  gavotte: "la ",
  gigue: "la ",
  gnossienne: "la ",
  gymnopédie: "la ",
  habanera: "la ",
  humoresque: "l'",
  hymne: "l'",
  impromptu: "l'",
  intermezzo: "l'",
  jeu: "le ",
  larghetto: "le ",
  largo: "le ",
  marche: "la ",
  mazurka: "la ",
  menuet: "le ",
  musette: "la ",
  musique: "la ",
  nocturne: "le ",
  ouverture: "l'",
  paraphrase: "la ",
  passacaille: "la ",
  pavane: "la ",
  pièce: "la ",
  polka: "la ",
  polonaise: "la ",
  prélude: "le ",
  première: "la ",
  promenade: "la ",
  récréation: "la ",
  rêve: "le ",
  rêverie: "la ",
  rhapsodie: "la ",
  romance: "la ",
  rondeau: "le ",
  rondo: "le ",
  sarabande: "la ",
  scherzo: "le ",
  sérénade: "la ",
  sicilienne: "la ",
  sonate: "la ",
  sonatine: "la ",
  suite: "la ",
  symphonie: "la ",
  tarentelle: "la ",
  thème: "le ",
  toccata: "la ",
  troisième: "la ",
  valse: "la ",
  variation: "la ",
};

/**
 * Le titre tel qu'on le cherche : sans l'apposition qui suit la virgule, quand
 * ce qui reste devant tient debout tout seul.
 */
export function searchableTitle(title: string): string {
  const comma = title.indexOf(",");

  if (comma === -1) {
    return title.trim();
  }

  const head = title.slice(0, comma).trim();

  return head.length >= MIN_HEAD_LENGTH ? head : title.trim();
}

/**
 * Vrai quand le titre se réduit à un seul mot : « Sarabande », « Prélude »,
 * « La Pastorale ».
 *
 * Trois morceaux du catalogue s'appellent « Sarabande » et deux « Prélude ».
 * Un titre pareil ne désigne rien sans son compositeur, et personne ne le tape
 * seul dans un moteur.
 */
export function isBareTitle(title: string): boolean {
  const name = searchableTitle(title);
  const lower = name.toLocaleLowerCase("fr");
  const determiner = LEADING_DETERMINERS.find((value) => lower.startsWith(value));
  const bare = (determiner === undefined ? name : name.slice(determiner.length)).trim();

  return bare !== "" && !bare.includes(" ");
}

/**
 * Le titre prêt à tomber dans « Apprends … au piano », article compris.
 *
 * `null` quand la phrase ne peut pas se former : apposition qu'on n'a pas pu
 * couper, ou titre qui ouvre sur une préposition.
 */
export function titlePhrase(title: string): string | null {
  const name = searchableTitle(title);

  if (name === "" || name.includes(",")) {
    return null;
  }

  const lower = name.toLocaleLowerCase("fr");
  const determiner = LEADING_DETERMINERS.find((value) => lower.startsWith(value));

  // Le titre porte déjà son déterminant : on le passe en bas de casse, parce
  // qu'il n'est plus en tête de phrase.
  if (determiner !== undefined) {
    return lower.slice(0, determiner.length) + name.slice(determiner.length);
  }

  const head = lower.split(/[\s'’]/, 1)[0];

  if (BLOCKING_OPENERS.has(head)) {
    return null;
  }

  const article = formArticle(head);

  return article === null ? name : `${article}${name}`;
}

/**
 * L'article d'un nom de forme, ou `null` si ce mot n'en est pas une.
 *
 * Le pluriel se déduit du singulier — « Danses polovtsiennes », « Variations
 * sur Thaxted » — plutôt que de doubler la table.
 */
function formArticle(head: string): string | null {
  if (head in FORM_ARTICLES) {
    return FORM_ARTICLES[head];
  }

  if (head.endsWith("s") && head.slice(0, -1) in FORM_ARTICLES) {
    return "les ";
  }

  return null;
}
